import Auth, { useUser } from '..';
import { BoxForm, Col, ImageBox, LogoArea } from './styled';
import { Submit } from '../../containers/form';
import React, { useCallback, useEffect, useState } from 'react';
import { setAuthorizationHeader } from 'donor/utils';
import { useHistory } from 'react-router-dom';
import { lookupCorrectEmail } from './emailLookup';

import style from './style.module.scss';
import { resendConfirmationCode } from './verifyUserApi';
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import Drafts from '@mui/icons-material/Drafts';
import AlternateEmail from '@mui/icons-material/AlternateEmail';
import { addGhostEmail } from './ghostEmailList';
import GhostsModal from './GhostsModal';
import TrueImpactText from '../../design-system/base/TrueImpactText';

function useGhostingLogin() {
  const [value, setValue] = useState();
  const handleKeyDown = useCallback(
    ({ keyCode, metaKey, ctrlKey, altKey }) => {
      if (keyCode === 192) setValue(value => !value);
      if (keyCode === 71 && (metaKey || ctrlKey) && altKey) {
        setValue(value => !value);
      }
    },
    [setValue],
  );
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return { ghostValue: value, setGhostValue: setValue };
}

export default function Ghosting({ onSignedIn, selectedEmail, returnPath }) {
  // eslint-disable-line
  const { ghostValue: ghostingLogin, setGhostValue } = useGhostingLogin();
  const effectiveUser = useUser();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const { push } = useHistory();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState(selectedEmail || '');
  const [password, setPassword] = useState('');
  const [ghostedEmail, setGhostedEmail] = useState('');
  const [openGhostModal, setOpenGhostModal] = useState(false);

  const handleEmailChange = useCallback(
    e => {
      setEmail(e.target.value);
    },
    [setEmail],
  );
  const handlePasswordChange = useCallback(
    e => {
      setPassword(e.target.value);
    },
    [setPassword],
  );
  const handleGhostedEmailChange = useCallback(
    e => {
      setGhostedEmail(e.target.value);
    },
    [setGhostedEmail],
  );

  const handleSubmit = useCallback(
    async ({ email, password, ghost }) => {
      try {
        effectiveUser.setPassword(password);
        effectiveUser.signInAs(ghost || null);
        setIsLoggingIn(true);
        setLoginError('');

        // Lookup correct email casing before signing in (case-insensitive login)
        const correctEmail = await lookupCorrectEmail(email.replace(/ /g, ''));
        const user = await Auth.signIn(correctEmail, password);
        if (user) {
          if (user && ghost) {
            if (user.attributes['custom:account_id'] !== '1') {
              setIsLoggingIn(false);

              // All of these, to make extra sure the following
              // will be executed immediately and not as a side effect
              // because the developer did not know any better.
              sessionStorage.removeItem('trueimpact.ghostedUsername');
              setAuthorizationHeader();
              await Auth.signOut();

              effectiveUser.signInAs(null);
              alert(`${email} is NOT an admin!`);
              return;
            }
            addGhostEmail({
              email: ghost,
              type: user.attributes['custom:type'],
            });
          }

          setAuthorizationHeader();
          push(returnPath || '/');
          onSignedIn();
          return;
        }
        return 'abort';
      } catch (e) {
        if (e.code === 'UserNotConfirmedException') {
          resendConfirmationCode(email);
        }
        setIsLoggingIn(false);
        setLoginError(e.message || 'Login failed. Please check your credentials and try again.');
        console.error(e);
        return false;
      }
    },
    [effectiveUser, returnPath, push],
  );

  const handleClickShowPassword = useCallback(() => {
    setShowPassword(s => !s);
  }, [setShowPassword]);
  const handleClickShowGhostModal = useCallback(() => {
    setOpenGhostModal(s => !s);
  }, [setOpenGhostModal]);
  const handleMouseDownPassword = useCallback(event => {
    event.preventDefault();
  }, []);
  const onSubmit = useCallback(
    e => {
      e.preventDefault();
      handleSubmit({ email, password, ghost: ghostedEmail });
    },
    [email, password, ghostedEmail, handleSubmit],
  );
  const pushSignup = () =>
    push(selectedEmail ? `/signup?email=${selectedEmail}` : '/signup');
  const pushForgot = () =>
    push(selectedEmail ? `/forgot?email=${selectedEmail}` : '/forgot');

  return (
    <div className={style.login_page__container}>
      <Col>
        <ImageBox />
        <LogoArea className={style.logo__wrapper}>
          <div className={style.logo} />
        </LogoArea>
      </Col>
      <Col className="form-side">
        <BoxForm className={`card ${style.login__wrapper}`}>
          <div className={style.label__wrapper}>
            <h1>{isLoggingIn ? 'Logging In...' : 'Log In'}</h1>
            <span className="regular">New to True Impact?</span>{' '}
            <span onClick={pushSignup} className={style.link}>
              Sign up here
            </span>
          </div>
          <form onSubmit={onSubmit}>
            <div className="form__input-group">
              <label htmlFor="email" className="form__label">
                Email
              </label>
              <TextField
                id="email"
                name="email"
                placeholder="Email"
                type="email"
                disabled={Boolean(selectedEmail)}
                value={email}
                onChange={handleEmailChange}
                fullWidth
                required
                autoComplete="username"
              />
            </div>
            <label htmlFor="password" className="form__label mt16">
              Password
            </label>
            <TextField
              className={style.password}
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              fullWidth
              required
              value={password}
              onChange={handlePasswordChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      id="toggle-password-visibility"
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {ghostingLogin && (
              <TextField
                name="ghost"
                placeholder="Ghost Email"
                fullWidth
                autoFocus={true}
                type="email"
                value={ghostedEmail}
                onChange={handleGhostedEmailChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle ghost emails visibility"
                        onClick={handleClickShowGhostModal}
                        edge="end"
                      >
                        {openGhostModal ? <Drafts /> : <AlternateEmail />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            {openGhostModal && (
              <GhostsModal
                handleEmailChange={setGhostedEmail}
                handleClose={handleClickShowGhostModal}
              />
            )}
            {loginError && (
              <div className={style.error}>{loginError}</div>
            )}
            <div className="loading-button">
              <Submit
                id="login-submit"
                className={style.submit}
                variant="contained"
                color="primary"
                type="submit"
                disabled={!email || !password || isLoggingIn}
              >
                Log In
              </Submit>
              {isLoggingIn && (
                <CircularProgress
                  size={30}
                  className="loading-button-loading"
                />
              )}
            </div>
            <div onClick={pushForgot} className={style.forgot}>
              Forgot your password?
            </div>
          </form>
        </BoxForm>
      </Col>
      {!ghostingLogin && (
        <div className={style.ghost_login_wrapper}>
          <TrueImpactText
            className={style.ghost_login}
            onClick={() => setGhostValue(s => !s)}
          >
            Ghost Login
          </TrueImpactText>
        </div>
      )}
    </div>
  );
}
